import { DisplayObject } from 'pixi.js'
import { openActivityModal } from '@/scripts/ui/activityModal'
import resizer, { ResizerDimensions } from '@/scripts/canvas/resizer'
import LayersContainer from '@/scripts/canvas/Layer/LayersContainer'
import { positions, isArchive } from '@/scripts/config/settings'
import { ANIMATION } from '@/scripts/config/constants'
import input from '@/scripts/canvas/Input'
import { isDesktopMode } from '@/scripts/helpers/features'
import { events } from '@/scripts/config/events'
import { initHashCheck } from '@/scripts/routing'
import activityInfoBox from '@/scripts/ui/activityInfoBox'
import buttons from '@/scripts/ui/buttons'
import type { Activity, Page } from '@/scripts/data/types'
import linkList from '@/scripts/ui/linkList'
import { initApp, setApp, registerPlugins, isLowEnd, parkScene, resumeScene, getApp } from './sceneManager'
import { onResize } from './stagePositioning'
import { showBullets, openMobileActivityInfoBox, openActivityInfoBox } from './sceneHelpers'
import { setupItemInteractions } from './itemInteractions'
import { SceneState } from './types'

export { parkScene, resumeScene }

export default function createScene(): void {
  const state: SceneState = {
    items: [],
    sortedItems: [],
    touchedItem: undefined,
    itemOverDelayedCall: undefined,
    phase2HideTimer: undefined,
    isDragging: false,
    moveCounter: 0,
  }

  linkList.renderDesktopLinks()
  const app = initApp()
  setApp(app)
  initResizer(state)
  initLayersContainer(state)
  prepareActivityInfoBox()
  attachButtonListeners(state)
  enableInteractivity(state)
  registerPlugins()

  if (app) input.init(app, positions.initialRatio)
  if (!isArchive)
    initHashCheck({
      onActivity: (activity: Activity) => {
        openActivityModal(activity, {
          onShow: () => {
            input.stop()
            app?.stop()
          },
          onClose: () => {
            input.start()
            app?.start()
          },
        })
      },
      onPage: (page: Page) => {
        openActivityModal(page, {
          onShow: () => {
            input.stop()
            app?.stop()
          },
          onClose: () => {
            input.start()
            app?.start()
          },
        })
      },
    })

  function initResizer(state: SceneState) {
    const canvasContainer = document.querySelector<HTMLDivElement>('.canvas-container')!
    resizer.init(canvasContainer)
    resizer.on(events.RESIZE, (dims: ResizerDimensions) => {
      const centeredItem = onResize(dims, state.items, state.touchedItem, (item) => {
        if (isDesktopMode()) {
          openActivityInfoBox(item)
        } else {
          openMobileActivityInfoBox(item, { current: state.itemOverDelayedCall })
        }
      })
      if (centeredItem) {
        state.touchedItem = centeredItem
      }
    })
    const dims = resizer.getDimensions()
    if (dims) {
      const centeredItem = onResize(dims, state.items, state.touchedItem, (item) => {
        if (isDesktopMode()) {
          openActivityInfoBox(item)
        } else {
          openMobileActivityInfoBox(item, { current: state.itemOverDelayedCall })
        }
      })
      if (centeredItem) {
        state.touchedItem = centeredItem
      }
    }
  }

  function initLayersContainer(state: SceneState) {
    const layersContainer = new LayersContainer(isLowEnd())
    const app = getApp()
    app?.stage.addChild(layersContainer as DisplayObject)
    layersContainer.startParallax()
    state.items = layersContainer.items
    state.sortedItems = [...state.items].sort((a, b) => a.activity.day - b.activity.day)
  }

  function prepareActivityInfoBox() {
    const container = document.querySelector<HTMLDivElement>(
      isDesktopMode() ? '.activity-info-box' : '.mobile-activity-info-box',
    )
    if (container) {
      activityInfoBox.init(container, {
        onShow: () => input.stop(),
        onHide: () => input.start(),
      })
    }
  }

  function attachButtonListeners(state: SceneState) {
    buttons.on(events.HELP_BUTTON_CLICK, (active: boolean) => {
      showBullets(state.items, active)
    })
  }

  function enableInteractivity(state: SceneState) {
    showBullets(state.items, true)
    setupItemInteractions(state)
    input.start()

    setTimeout(() => showBullets(state.items, false), ANIMATION.BULLETS_HIDE_DELAY)
  }
}
